<script lang="ts">
    import {
        FullscreenControl,
        GeoJSONSource,
        LngLat,
        NavigationControl,
    } from "maplibre-gl";
    import { appManager } from "../../AppManager";
    import { afterUpdate } from "svelte";
    import { v4 as uuidv4 } from "uuid";
    import type {
        Feature,
        FeatureCollection,
        LineString,
        MultiPolygon,
        Point,
        Polygon,
    } from "geojson";
    import * as turf from "@turf/turf";
    import { ShortCutClassicLayer } from "../../map/layers/ShortCutClassicLayer";
    import { _ } from "svelte-i18n";
    import { ModalHook } from "../../shared/modals/ModalHook";
    import type { MatrixNewData } from "../../api/projects/data/matrix/MatrixNewData";
    import type { MapManagerOptions } from "../../lib/maplibre/MapManagerOptions";
    import { MapManager } from "../../lib/maplibre/MapManager";
    import { MapLibreDraw } from "@birkskyum/maplibre-gl-draw";
    import "@birkskyum/maplibre-gl-draw/dist/maplibre-gl-draw.css";
    import { Dictionary } from "../../shared/Dictionary";
    import { MatrixLocationNewData } from "../../api/projects/data/matrix/MatrixLocationNewData";
    import { MatrixElementNewData } from "../../api/projects/data/matrix/MatrixElementNewData";
    import { ActivityType } from "../../api/demand-model/data/ActivityType";
    import { randomLocations } from "../../shared/turf/RandomLocations";
    import TinyHelp from "../../shared/help/TinyHelp.svelte";
    import { toolTip } from "../../shared/help/ToolTip";
    import { get, writable, Writable } from "svelte/store";
    import TrashIcon from "../../shared/icons/TrashIcon.svelte";
    import MapAlert from "../../shared/map/MapAlert.svelte";
    import MapBusyAlert from "../../shared/map/MapBusyAlert.svelte";

    let hook: ModalHook<
        {
            zoom: number;
            center: LngLat;
        },
        MatrixNewData
    > = new ModalHook<
        {
            zoom: number;
            center: LngLat;
        },
        MatrixNewData
    >();
    appManager.ModalHooks.DemandModal = hook;
    let randomLocationsApi = appManager.api.randomLocations;
    let open: boolean = false;
    let mapContainer: HTMLElement;
    let working: boolean = false;
    let canSave: boolean = false;
    let locationsCount: number = 0;
    let movementsCount: number = 0;
    const draw = new MapLibreDraw({
        displayControlsDefault: false,
        controls: {},
    });
    let lastDirty = 0;
    let useRandomLocationsApi: Writable<boolean> = writable(false);
    useRandomLocationsApi.subscribe(async (v) => {
        setConfigDirty();
    });
    let matrix: MatrixNewData = undefined;
    let distance: Writable<number> = writable(75);
    distance.subscribe(async (v) => {
        await generateMatrix();
    });
    let step: "draw_polygon" | "fine_tune" = "draw_polygon";
    let demandPerActivityType: {
        type: ActivityType;
        count: number;
        trips: number;
        distance: number;
    }[] = [
        {
            type: "work",
            count: 100,
            trips: 1,
            distance: 75000,
        },
        {
            type: "shopping_daily",
            count: 0,
            trips: 1,
            distance: 5000,
        },
        {
            type: "shopping_long_term",
            count: 0,
            trips: 1,
            distance: 40000,
        },
        {
            type: "private_visit",
            count: 0,
            trips: 1,
            distance: 75000,
        },
    ];
    // tooltips.
    const populationDensity = toolTip.New({
        content: $_(
            "impact.scenario.demandModelModal.populationDensity.help.tooltip",
        ),
    });

    // create the map.
    let mapOptions: MapManagerOptions = {
        imageryLayers: [],
        baseLayer: new ShortCutClassicLayer(),
    };
    let mapManager: MapManager;

    // hook events.
    hook.on("open", () => {
        mapManager = new MapManager(mapOptions);

        open = true;
    });
    hook.on("close", () => {
        open = false;
    });

    const modalClose = () => {
        hook.close(undefined);
    };

    afterUpdate(async () => {
        if (open) {
            if (mapManager.loaded()) return;

            const view = hook.getData();

            await mapManager.initialize(
                mapContainer,
                async (e, map) => {
                    mapManager.addControl(
                        new NavigationControl({}),
                        "top-right",
                    );
                    mapManager.addControl(
                        new FullscreenControl({}),
                        "top-right",
                    );
                    mapManager.addSource("locations", {
                        type: "geojson",
                        data: turf.featureCollection([]) as GeoJSON.GeoJSON,
                    });

                    mapManager.addLayer({
                        id: "locations-back",
                        type: "circle",
                        source: "locations",
                        layout: {
                            visibility: "visible",
                        },
                        paint: {
                            "circle-color": "#000000",
                            "circle-radius": 8,
                        },
                    });
                    mapManager.addLayer({
                        id: "locations",
                        type: "circle",
                        source: "locations",
                        layout: {
                            visibility: "visible",
                        },
                        paint: {
                            "circle-color": "#ffc000",
                            "circle-stroke-width": 0,
                            "circle-radius": 6,
                        },
                    });

                    mapManager.addSource("movements", {
                        type: "geojson",
                        data: turf.featureCollection([]) as GeoJSON.GeoJSON,
                    });

                    mapManager.addLayer(
                        {
                            id: "movement-lines",
                            type: "line",
                            source: "movements",
                            layout: {
                                "line-join": "round",
                                "line-cap": "round",
                            },
                            paint: {
                                "line-color": "#000",
                                "line-width": 2,
                                "line-offset": 8,
                                "line-opacity": 0.3,
                            },
                        },
                        "symbol",
                    );

                    map.addControl(draw, "top-left");
                    draw.changeMode("draw_polygon");

                    map.on("draw.modechange", async (e) => {
                        const mode = draw.getMode();
                        console.log("draw.modechange: " + mode);
                        if (mode !== "simple_select") return;

                        await generateMatrix();
                    });
                    // map.on("draw.create", async () => );
                    // map.on("draw.delete", async () => await generateMatrix());
                    map.on("draw.update", async (e) => {
                        const mode = draw.getMode();
                        console.log("draw.update: " + mode);
                        if (mode !== "simple_select") return;

                        await generateMatrix();
                    });
                },
                view,
            );

            startDirtyTrigger();
        }
    });

    function save() {
        hook.close(matrix);
    }

    function setConfigDirty() {
        lastDirty = Date.now();
        abortController?.abort();
    }
    function startDirtyTrigger() {
        setInterval(async () => {
            if (lastDirty == 0) return;

            const now = Date.now();
            if (now - lastDirty <= 1000) {
                return;
            }

            lastDirty = 0;
            await generateMatrix();
        }, 1000);
    }

    let abortController: AbortController;
    function cancelGenerateMatrix(): void {
        abortController?.abort();

        canSave = false;
        working = false;
    }
    async function generateMatrix(): Promise<void> {
        let data = draw.getAll() as FeatureCollection;
        if (typeof data === "undefined") return;
        if (typeof data.features === "undefined") return;
        if (data.features.length < 1) {
            return;
        }
        if (data.features.length < 2) {
            const first = data.features[0];
            const second = turf.simplify(
                turf.buffer(first, $distance, {
                    units: "kilometers",
                    steps: 2,
                }),
                { tolerance: 0.1, mutate: true },
            );
            data = turf.featureCollection([first, second]);
            draw.set(data);
        }

        step = "fine_tune";

        if (typeof abortController !== "undefined") {
            abortController.abort();
        }
        abortController = new AbortController();
        const signal = abortController.signal;

        canSave = false;
        working = true;
        const locationsSource = mapManager.getSource(
            "locations",
        ) as GeoJSONSource;
        const movementsSource = mapManager.getSource(
            "movements",
        ) as GeoJSONSource;
        if (
            typeof movementsSource === "undefined" ||
            typeof locationsSource === "undefined"
        ) {
            return;
        }
        locationsSource.setData(turf.featureCollection([]));
        movementsSource.setData(turf.featureCollection([]));

        if (data.features.length <= 0) return;

        let locations: FeatureCollection;
        locationsCount = demandPerActivityType
            .map((x) => x.count)
            .reduce((sum, current) => sum + current, 0);
        if (get(useRandomLocationsApi)) {
            locations = await randomLocationsApi.postPolygon({
                polygon: data.features[0].geometry as Polygon,
                count: locationsCount,
                type: "residential",
                signal: signal,
            });
            if (signal.aborted) return;
        } else {
            locations = randomLocations.inPolygon(
                data.features[0],
                locationsCount,
            );
        }
        console.log(locations);
        console.log(locationsCount);

        let i = 0;
        movementsCount = 0;
        demandPerActivityType.forEach((demand) => {
            for (let c = 0; c < demand.count; c++) {
                if (c < locations.features.length) {
                    const location = locations.features[i];
                    location.properties["activity_type"] = demand.type;
                    location.properties["count"] = demand.trips;
                    location.properties["distance"] = demand.distance;
                    movementsCount += demand.trips;
                }

                i++;
            }
        });

        locationsSource.setData(locations);

        const movements =
            await appManager.api.demandModel.postEstimateLocations({
                data: {
                    demandArea: data.features[1].geometry,
                    locations: locations,
                },
                defaultActivityType: "work",
                defaultCount: 1,
                defaultDistance: 75000,
                signal: signal,
            });
        if (signal.aborted) return;
        movementsSource.setData(movements);

        matrix = {
            commitCommit: "New demand model matrix.",
            elements: [],
            locations: [],
            name: "Home -> Work",
            properties: {},
            visible: true,
        };

        const matrixLocations: Dictionary<number> = new Dictionary<number>();
        const matrixMovements: Dictionary<MatrixElementNewData> =
            new Dictionary<MatrixElementNewData>();
        movements.features.forEach((movement) => {
            const line = movement.geometry as LineString;
            const from = line.coordinates[0];
            const fromKey = `${from[0]},${from[1]}`;
            let fromIdx = -1;
            if (!matrixLocations.containsKey(fromKey)) {
                fromIdx = matrix.locations.length;
                matrixLocations.add(fromKey, fromIdx);
                matrix.locations.push({
                    longitude: from[0],
                    latitude: from[1],
                    properties: {},
                });
            } else {
                fromIdx = matrixLocations.get(fromKey);
            }

            const to = line.coordinates[line.coordinates.length - 1];
            const toKey = `${to[0]},${to[1]}`;
            let toIdx = -1;
            if (!matrixLocations.containsKey(toKey)) {
                toIdx = matrix.locations.length;
                matrixLocations.add(toKey, toIdx);
                matrix.locations.push({
                    longitude: to[0],
                    latitude: to[1],
                    properties: {},
                });
            } else {
                toIdx = matrixLocations.get(toKey);
            }

            const movementKey = `${fromKey}->${toKey}`;
            if (matrixMovements.containsKey(movementKey)) {
                const matrixMovement = matrixMovements.get(movementKey);
                matrixMovement.count += 1;
            } else {
                matrixMovements.add(movementKey, {
                    count: 1,
                    fromIdx: fromIdx,
                    toIdx: toIdx,
                    properties: {},
                });
            }
        });

        matrix.elements.push(...matrixMovements.values());

        canSave = true;
        working = false;
    }
</script>

{#if open}
    <div class="modal show" style="display: block;">
        <div class="modal-dialog modal-fullscreen">
            <div class="modal-content">
                <div class="modal-body p-0">
                    <div class="container-fluid p-0 h-100">
                        <div class="row g-0 h-100">
                            <div class="col-8">
                                <div class="map w-100 h-100">
                                    <div
                                        class="w-100 h-100"
                                        bind:this={mapContainer}
                                    >
                                        <slot />
                                    </div>
                                    {#if !working}
                                        <MapAlert>
                                            {#if step == "draw_polygon"}
                                                Draw polygon on the map to
                                                select home locations.
                                            {:else if step == "fine_tune"}
                                                <div>
                                                    We have generated {movementsCount}
                                                    trips starting at {locationsCount}
                                                    home locations.
                                                    <br />
                                                    Edit the polygons to finetune
                                                    the result.
                                                </div>
                                            {/if}
                                        </MapAlert>
                                    {:else}
                                        <MapBusyAlert>
                                            Generating trips... please wait or <button
                                                class="btn btn-small btn-secondary"
                                                on:click={cancelGenerateMatrix}
                                                >cancel</button
                                            >.
                                        </MapBusyAlert>
                                    {/if}
                                </div>
                            </div>
                            <div class="col-4 p-2">
                                <button
                                    type="button"
                                    class="btn-close btn-lg btn-close-lg"
                                    aria-label="Close"
                                    on:click={modalClose}
                                />
                                <button
                                    type="button"
                                    class="btn btn-save btn-secondary {canSave
                                        ? ''
                                        : 'disabled'}"
                                    data-bs-dismiss="modal"
                                    on:click={save}
                                    >{$_(
                                        "impact.scenario.demandModelModal.save",
                                    )}
                                </button>
                                <div class="container-fluid pt-4 h-100">
                                    <div class="row align-items-center pb-2">
                                        <div class="col">
                                            <h4>
                                                {$_(
                                                    "impact.scenario.demandModelModal.title",
                                                )}
                                            </h4>
                                            {$_(
                                                "impact.scenario.demandModelModal.explanation",
                                            )}
                                        </div>
                                    </div>
                                    {#each demandPerActivityType as activity}
                                        <div class="row border">
                                            <div class="col">
                                                <input
                                                    type="number"
                                                    bind:value={activity.count}
                                                    min="0"
                                                    class="counter"
                                                    on:change={setConfigDirty}
                                                />
                                                each having
                                                <input
                                                    type="number"
                                                    bind:value={activity.trips}
                                                    min="1"
                                                    class="counter"
                                                    on:change={setConfigDirty}
                                                />
                                                trip(s) going to {activity?.type}
                                            </div>
                                        </div>
                                    {/each}

                                    <div class="row">
                                        <div class="col">
                                            <label
                                                class="mb-8 mt-2"
                                                bind:this={$populationDensity}
                                            >
                                                <input
                                                    bind:checked={$useRandomLocationsApi}
                                                    type="checkbox"
                                                />
                                                {$_(
                                                    "impact.scenario.demandModelModal.populationDensity.description",
                                                )}
                                                <TinyHelp />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .map {
        border-radius: 0.5rem;
        position: relative;
        bottom: 0px;
        left: 0px;
        right: 0px;
    }

    .map-control {
        position: absolute;
        top: 10px;
        left: 10px;
    }

    .counter {
        width: 70px;
    }

    .modal {
        display: block;
    }
</style>
